import React, { useState, useEffect } from 'react';
import { Heart, Target, Eye, Users, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { usePageTitle } from '../components/PageTitle';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Heart: <Heart className="w-10 h-10" />,
  Target: <Target className="w-10 h-10" />,
  Eye: <Eye className="w-10 h-10" />,
  Users: <Users className="w-10 h-10" />
};

const About = () => {
  usePageTitle('About Us | Lehigh Human Support Services');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default content (fallback)
  const defaultContent = {
    hero_title: 'About Lehigh Human Support Services',
    hero_subtitle: 'Empowering Lives, Fostering Community and Family Support',
    story_title: 'Our Story',
    story_paragraphs: [
      'Located in Allentown, Pennsylvania, Lehigh Human Support Services has been a beacon of hope and support for individuals with intellectual and developmental disabilities for over 15 years.',
      'We believe that everyone deserves the opportunity to live a fulfilling life, pursue their dreams, and contribute to their community. Our dedicated team of Direct Support Professionals works tirelessly to make this vision a reality.',
      'From residential support to community integration programs, we provide comprehensive services that empower individuals to achieve independence while maintaining the support they need to thrive.'
    ],
    story_image: 'https://images.unsplash.com/photo-1520857014576-2c4f4c972b57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxjb21tdW5pdHklMjBzdXBwb3J0fGVufDB8fHx8MTc2ODgwNjk3MXww&ixlib=rb-4.1.0&q=85',
    mission_text: 'To provide compassionate, person-centered support services that empower individuals with intellectual and developmental disabilities to live independently, pursue their goals, and actively participate in their communities.',
    vision_text: 'A world where individuals with intellectual and developmental disabilities are fully included, valued, and supported to reach their highest potential and live fulfilling lives on their own terms.',
    values_title: 'Our Core Values',
    values_subtitle: 'The principles that guide everything we do',
    values: [
      { id: 'v1', icon: 'Heart', title: 'Compassion', description: 'We approach every interaction with empathy, kindness, and genuine care for the individuals we serve.' },
      { id: 'v2', icon: 'Users', title: 'Respect', description: 'We honor the dignity, choices, and individuality of each person, fostering an environment of mutual respect.' },
      { id: 'v3', icon: 'Target', title: 'Excellence', description: 'We are committed to providing the highest quality of care through continuous improvement and professional development.' },
      { id: 'v4', icon: 'Eye', title: 'Integrity', description: 'We operate with transparency, honesty, and ethical practices in all aspects of our service delivery.' }
    ],
    team_section_title: 'Our Dedicated Team',
    team_section_text: [
      'At the heart of Lehigh HSS is our incredible team of Direct Support Professionals, coordinators, and administrative staff. Each member brings unique skills, compassion, and dedication to our mission.',
      'We invest in ongoing training and professional development to ensure our team provides the highest quality of care and support to every individual we serve.'
    ],
    team_images: [
      'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBzdXBwb3J0fGVufDB8fHx8MTc2ODgwNjk3MXww&ixlib=rb-4.1.0&q=85',
      'https://images.unsplash.com/photo-1581056771107-24ca5f033842?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxoZWFsdGhjYXJlJTIwY2FyZWdpdmVyc3xlbnwwfHx8fDE3Njg4MDY5NjV8MA&ixlib=rb-4.1.0&q=85',
      'https://images.unsplash.com/photo-1631217868204-db1ed6bdd224?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxoZWFsdGhjYXJlJTIwY2FyZWdpdmVyc3xlbnwwfHx8fDE3Njg4MDY5NjV8MA&ixlib=rb-4.1.0&q=85'
    ],
    contact_title: 'Get to Know Us Better',
    contact_subtitle: 'Located in Allentown, Pennsylvania, we serve the Lehigh Valley community with dedication and care.'
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/about`);
      const merged = { ...defaultContent, ...response.data };
      // Ensure arrays exist
      if (!merged.story_paragraphs || merged.story_paragraphs.length === 0) {
        merged.story_paragraphs = defaultContent.story_paragraphs;
      }
      if (!merged.values || merged.values.length === 0) {
        merged.values = defaultContent.values;
      }
      if (!merged.team_section_text || merged.team_section_text.length === 0) {
        merged.team_section_text = defaultContent.team_section_text;
      }
      if (!merged.team_images || merged.team_images.length === 0) {
        merged.team_images = defaultContent.team_images;
      }
      setContent(merged);
    } catch (error) {
      console.error('Error fetching about content:', error);
      setContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const { values, story_paragraphs, team_section_text, team_images } = content;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-50 to-white py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              {content.hero_title}
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              {content.hero_subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">{content.story_title}</h2>
              {story_paragraphs.map((paragraph, index) => (
                <p key={index} className="text-lg text-gray-700 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="relative">
              <img
                src={content.story_image}
                alt="Team support"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-2 border-amber-200 bg-white hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-10 space-y-4">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Our Mission</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {content.mission_text}
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-amber-200 bg-white hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-10 space-y-4">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center">
                  <Eye className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Our Vision</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {content.vision_text}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">{content.values_title}</h2>
            <p className="text-xl text-gray-600">{content.values_subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-amber-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8 space-y-4 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-50 rounded-full text-amber-500">
                    {iconMap[value.icon] || <Heart className="w-10 h-10" />}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="grid grid-cols-2 gap-4">
              {team_images.slice(0, 3).map((image, index) => (
                <div key={index} className={`${index === 0 ? 'col-span-2' : ''}`}>
                  <img
                    src={image}
                    alt={`Team photo ${index + 1}`}
                    className={`rounded-xl shadow-lg w-full object-cover ${index === 0 ? 'h-64' : 'h-48'}`}
                  />
                </div>
              ))}
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">{content.team_section_title}</h2>
              {team_section_text.map((text, index) => (
                <p key={index} className="text-lg text-gray-700 leading-relaxed">
                  {text}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Contact Info */}
      <section className="bg-gray-900 py-20">
        <div className="container mx-auto px-4 lg:px-8 text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            {content.contact_title}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {content.contact_subtitle}
          </p>
          
          {/* Contact Info Card */}
          <div className="max-w-2xl mx-auto mt-8">
            <div className="bg-amber-50 rounded-2xl p-8 text-gray-800 space-y-4">
              <div className="space-y-3">
                <p className="text-lg">
                  <span className="font-bold text-gray-900">Address:</span> {content.office_address || '5920 Hamilton Blvd, Suite 108, Allentown, PA 18106'}
                </p>
                <p className="text-lg">
                  <span className="font-bold text-gray-900">Phone:</span> {content.office_phone || '(484) 350-3601'}
                </p>
                <p className="text-lg">
                  <span className="font-bold text-gray-900">Fax:</span> {content.office_fax || '484-350-3440'}
                </p>
                <p className="text-lg">
                  <span className="font-bold text-gray-900">Email:</span> {content.office_email || 'Info@Lehigh-hss.com'}
                </p>
                <p className="text-lg">
                  <span className="font-bold text-gray-900">Hours:</span> {content.office_hours || 'Monday – Friday, 9:00 AM – 6:00 PM'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
